import { adminApi, parseApiError } from './api';
import type {
  AdminUser,
  AdminUserDetail,
  AdminUserFilters,
  AdminUserListResponse,
} from '@/types/admin.types';

export const usersService = {
  /**
   * Get paginated list of users with filters
   */
  async getUsers(filters?: AdminUserFilters): Promise<AdminUserListResponse> {
    try {
      const params = new URLSearchParams();

      if (filters?.search) params.append('search', filters.search);
      if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters?.plan) params.append('plan', filters.plan);
      if (filters?.country) params.append('country', filters.country);

      // Convert page to offset
      const limit = filters?.limit || 20;
      const page = filters?.page || 1;
      const offset = (page - 1) * limit;

      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      if (filters?.sortBy) params.append('sortBy', filters.sortBy);
      if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

      const { data } = await adminApi.get<AdminUserListResponse>(
        `/admin/users?${params.toString()}`
      );

      return {
        success: data.success,
        users: data.users || [],
        total: data.total || 0,
        limit: data.limit || limit,
        offset: data.offset || offset,
      };
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Get user details by ID
   */
  async getUser(userId: string): Promise<AdminUserDetail | null> {
    try {
      const { data } = await adminApi.get<{ success?: boolean; user?: AdminUserDetail } & AdminUserDetail>(`/admin/users/${userId}`);
      // API may return { success, user: {...} } or the user directly
      return data.user || data;
    } catch (error) {
      const err = parseApiError(error);
      // Return null if user not found or endpoint doesn't exist
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        return null;
      }
      throw err;
    }
  },

  /**
   * Update user data
   */
  async updateUser(userId: string, updates: Partial<AdminUser>): Promise<AdminUser> {
    try {
      const { data } = await adminApi.patch<AdminUser>(`/admin/users/${userId}`, updates);
      return data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Change user's plan
   */
  async changePlan(userId: string, planId: string): Promise<void> {
    try {
      await adminApi.post(`/admin/users/${userId}/change-plan`, { planId });
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Cancel user's subscription
   */
  async cancelSubscription(userId: string, immediate?: boolean): Promise<void> {
    try {
      await adminApi.post(`/admin/users/${userId}/cancel-subscription`, { immediate });
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Extend user's trial
   */
  async extendTrial(userId: string, days: number): Promise<void> {
    try {
      await adminApi.post(`/admin/users/${userId}/extend-trial`, { days });
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Get user's usage history
   */
  async getUserUsage(userId: string, months?: number): Promise<AdminUserDetail['usageHistory']> {
    try {
      const { data } = await adminApi.get(`/admin/users/${userId}/usage`, {
        params: { months: months || 6 },
      });
      return data.usage;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Get user's billing history
   */
  async getUserBilling(userId: string): Promise<AdminUserDetail['billingHistory']> {
    try {
      const { data } = await adminApi.get(`/admin/users/${userId}/billing`);
      return data.events;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Export users to CSV
   */
  async exportUsers(filters?: AdminUserFilters): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters?.plan) params.append('plan', filters.plan);
      if (filters?.country) params.append('country', filters.country);

      const { data } = await adminApi.get(`/admin/users/export?${params.toString()}`, {
        responseType: 'blob',
      });
      return data;
    } catch (error) {
      throw parseApiError(error);
    }
  },
};

export default usersService;
